<template>
  <div class="glass-production-station">
    <div class="station-topbar">
      <div :class="{'alert': true, 'alert-warning': !pageErrorInfo.isError, 'alert-danger': pageErrorInfo.isError}" v-if="pageErrorInfo">
          <i class="fas fa-exclamation-triangle"></i> {{pageErrorInfo.message}}
          <a class="float-right" href="#" @click.prevent="reloadPage" v-if="pageErrorInfo.retryAvailable">Try Again</a>
        </div>
      <StationTitleBar v-if="stationLoaded" :stageColour="productionStage.highlightColour" :stageName="productionStage.name" :stationName="productionStation.name">
      </StationTitleBar>
    </div>

    <div class="station-content">
      <StationTabbedPage v-if="stationLoaded">
        <template v-slot:tablist>
          <StationTabButton name="Main" icon="fa-desktop" :badge="urgentPanelCount" />
          <StationTabButton name="Search" icon="fa-search" />
          <StationTabButton name="Stats" icon="fa-analytics" />
          <StationTabButton name="Info" icon="fa-info-circle" />
        </template>
        <template v-slot:tabcontent>
          <!-- Main Station -->
          <StationTabContent class="station-main-panel">
            <div class="station-leftbar" v-if="displayLeftBarList">
              <div class="station-leftbar-graph">
                <StationPerformanceGraph ref="performanceChart" :productionStation="productionStation" v-if="productionStation.showStationGraph" />
              </div>
              <StationButtonGroup>
                <StationButtonGroupButton class="col-6" :active="scheduleTabIndex == 0" @click="scheduleTabIndex = 0">
                  PANELS
                </StationButtonGroupButton>
                <StationButtonGroupButton class="col-6" :active="scheduleTabIndex == 1" @click="scheduleTabIndex = 1">
                  ORDERS
                </StationButtonGroupButton>
              </StationButtonGroup>
              
              <div class="station-leftbar-list station-leftbar-list-with-button-group" ref="scheduleLeftBar">
                <StationPanelScheduleList 
                  v-show="scheduleTabIndex == 0"
                  :stationId="$route.params.id"
                  @panelClick="panelClicked"
                  @urgentCountUpdated="urgentPanelCount = $event"
                />

                <StationOrderScheduleList 
                  v-show="scheduleTabIndex == 1"
                  :stationId="$route.params.id"
                  @orderClick="orderClicked"
                />
              </div>
            </div>
            <div :class="['station-mainarea', { 'station-mainarea-fullpage' : !displayLeftBarList }]">
              <div class="station-tool-window">
                <component
                  :is="stationMainViewComponent"
                  v-if="stationLoaded"
                  :productionStage="productionStage"
                  :productionStation="productionStation"
                  @refreshStation="requestStationData" 
                  @panelPreviewClick="panelPreviewClicked"
                  @panelClick="panelClicked"
                  @orderClick="orderClicked"
                  @urgentCountUpdated="urgentPanelCount = $event"
                />
                <h1 v-if="!stationMainViewComponent" class="text-center">Station is unavailable</h1>
              </div>
            </div>
            <SchedulePanelPopover
              ref="panelPopover"
              @orderClick="displayPanelOrder($event)"
              @orderItemsClick="displayPanelOrderItems($event)"
              @orderDocumentsClick="displayPanelOrderDocuments($event)"
              @orderEditorViewClick="displayPanelOrderEditorView($event)"
              @printLabelClick="printPanelLabel($event)"
              @reprintLabelClick="reprintPanelLabel($event)"
              @recutClick="displayPanelRecut($event, productionStation.id)"
              @revertClick="displayPanelRevert($event, productionStation.id)"
              />
            <ScheduleOrderPopover
              ref="orderPopover"
              @orderClick="displayOrder($event)"
              @orderItemsClick="displayOrderOrderItems($event)"
              @orderDocumentsClick="displayOrderDocuments($event)"
              />
            
          </StationTabContent>

          <!-- Search -->
          <StationTabContent class="station-main-panel">
            <StationSearchPageView :productionStageId="productionStage.id" :productionStationId="productionStation.id" />
          </StationTabContent>

          <!-- Stats -->
          <StationTabContent class="station-main-panel">
            <StationPerformancePageView :productionStationId="productionStation.id" />
          </StationTabContent>

          <!-- Info -->
          <StationTabContent class="station-main-panel">
            <StationInfoPageView :productionStationId="productionStation.id" />
          </StationTabContent>

        </template>
      </StationTabbedPage>
    </div>
  </div>
</template>

<style lang="scss">
@import "@/apps/glass-production-station/src/scss/station.scss";
</style>

<script>
import { mapState } from 'vuex'

import pdfMixin from '@/mixins/pdf'
import themeMixin from '@/mixins/theme'
import productionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'

import StationTitleBar from './../components/main/stationTitleBar'
import StationButtonGroup from '@/apps/glass-production-station/src/components/main/stationButtonGroup'
import StationButtonGroupButton from '@/apps/glass-production-station/src/components/main/stationButtonGroupButton'
import StationPanelScheduleList from './../components/schedule/stationPanelScheduleList'
import StationOrderScheduleList from './../components/schedule/stationOrderScheduleList'
import SchedulePanelPopover from './../components/schedule/schedulePanelPopover'
import ScheduleOrderPopover from './../components/schedule/scheduleOrderPopover'
import DocumentPreviewDialog from '@/components/dialog/documentPreviewDialog'
import OrderItemsPreviewDialog from './orderItemsPreviewDialog'
import PreviewPanelDialog from './previewPanelDialog'

import StationTabbedPage from '@/apps/glass-production-station/src/components/main/stationTabbedPage'
import StationTabContent from '@/apps/glass-production-station/src/components/main/stationTabContent'
import StationTabButton from '@/apps/glass-production-station/src/components/main/stationTabButton'

import StationPerformanceGraph from './../components/stationPerformanceGraph'

import GeneralStationView from './generalStationView'
import ManualCuttingStationView from './manualCuttingStationView'
import CuttingOptimiserStationView from './optimiser/cuttingOptimiserStationView'
import TougheningStationView from './tougheningStationView'
import WaterjetStationView from './waterjetStationView'

import StationPerformancePageView from './stationPerformancePageView'
import StationSearchPageView from './stationSearchPageView'
import StationInfoPageView from './stationInfoPageView'

import { ProductionStationTypes } from '@/store/types'

export default {
  name: 'GlassProductionStationApp',
  mixins: [
    pdfMixin,
    themeMixin,
    productionStationMixin
  ],
  components: {
    StationTitleBar,
    StationButtonGroup,
    StationButtonGroupButton,
    StationPanelScheduleList,
    StationOrderScheduleList,
    StationPerformanceGraph,
    SchedulePanelPopover,
    ScheduleOrderPopover,

    StationTabbedPage,
    StationTabButton,
    StationTabContent,

    // Stations
    GeneralStationView,
    ManualCuttingStationView,
    CuttingOptimiserStationView,
    TougheningStationView,
    WaterjetStationView,

    // Tab Pages
    StationSearchPageView,
    StationPerformancePageView,
    StationInfoPageView
  },  
  data: () => {
    return {
      scheduleTabIndex: 0,
      themeClass: 'theme-dark',
      
      stationLoaded: false,
      urgentPanelCount: 0,
      availablePanelCount: 0,
      productionStage: {},
      productionStation: {},
    }
  },
  computed: {
    ...mapState({
      pageErrorInfo: state => state.page.pageError
    }),
    stationMainViewComponent() {
      let accountHasFurnace = this.accountHasProductFeature(this.AccountProductFeatureTypes.productionFurnace)
      let accountHasOptimisation = this.accountHasProductFeature(this.AccountProductFeatureTypes.productionOptimisation)
      let accountHasOrderEditor = this.accountHasProductFeature(this.AccountProductFeatureTypes.orderEditor)

      switch(this.productionStation.type) {
        // Manual Cutting
        case ProductionStationTypes.cutting:
          return 'ManualCuttingStationView'

        // Cutting - Optimisation - Requires ProductionOptimisation feature on the account
        case ProductionStationTypes.cuttingOptimisation:
          if(!accountHasOptimisation)
            return null
          return 'CuttingOptimiserStationView'
        case ProductionStationTypes.cuttingBreakout:
          if(!accountHasOptimisation)
            return null
          return 'CuttingOptimiserStationView'

        case ProductionStationTypes.general:
        case ProductionStationTypes.feedIn:
          return 'GeneralStationView'

        // Furnace - Requires ProductionFurnace feature on the account
        case ProductionStationTypes.furnace:
          if(!accountHasFurnace)
            return null
          return 'TougheningStationView'
        case ProductionStationTypes.furnaceUnload:
          if(!accountHasFurnace)
            return null
          return 'TougheningStationView'

        // Waterket - Requires OrderEditor feature on the account
        case ProductionStationTypes.waterjet:
          if(!accountHasOrderEditor)
            return null
          return 'WaterjetStationView'
      }
      return null
    },
    displayLeftBarList() {
      // Cutting Optimisation / Breakout station displays its own leftbar lists
      if(this.productionStation.type == ProductionStationTypes.cuttingOptimisation || this.productionStation.type == ProductionStationTypes.cuttingBreakout)
        return false
      return true
    }
  },
  mounted() {
    this.stationLoaded = false
    
    // Require account settings before loading/processing anything else
    this.requestAccountData()
    .then(() => {
      this.requestStationData()
    })
    .catch(err => {
      this.handlePageLoadError(err)
    })
    
    this.subscribeEndpoint(`/production/stations/${this.$route.params.id}`, () => {
      this.requestStationData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/panelschedule')
    this.unsubscribeEndpoint(`/production/stations/${this.$route.params.id}`)
  },
  methods: {
    requestAccountData() {
      return new Promise(async (resolve, reject) => {
        try {
          let results = await Promise.all([
            this.$api.get("/profile"),
            this.$apiService.adminService.getAccount()
          ])

          this.$store.commit("profile/setProfile", results[0].data);
          this.$store.commit("account/setAccountData", results[1].data)

          return resolve()
        } catch(err) {
          return reject(err)
        }
      })
    },
    // Load the station data required for the app
    requestStationData() {
      this.pageLoadData(Promise.all([
        this.$apiService.productionService.getProductionStage(this.$route.params.stageId),
        this.$apiService.productionService.getProductionStation(this.$route.params.id),
      ]))
      .then(results => {
        this.productionStage = results[0].data
        this.productionStation = results[1].data

        this.stationLoaded = true
      })
      .catch(err => this.handlePageLoadError(err))
    },

    reloadPage() {
      this.$router.go(0)
    },

    panelPreviewClicked(evt) {
      this.displayModal(PreviewPanelDialog, { 
        productionPanelId: parseInt(evt.panel.id),
      })
      .then(() => {
      })      
    },
    
    panelClicked(evt) {
      if(evt.forceElement) {
        this.$refs.panelPopover.displayFromElement(evt.target, evt.panel)      
        return
      }

      // JB: Unless forced, we just display from the schedule bar
      // Currently moving a visible popover (when click on a panel, then another without closing) does not work well at all & looks wierd
      // So we just show to the schedule bar
      this.$refs.panelPopover.displayFromElement(this.$refs.scheduleLeftBar, evt.panel)
    },
    orderClicked(evt) {
      if(evt.forceElement) {
        this.$refs.orderPopover.displayFromElement(evt.target, evt.order)      
        return
      }

      // JB: Unless forced, we just display from the schedule bar
      // Currently moving a visible popover (when click on a order, then another without closing) does not work well at all & looks wierd
      // So we just show to the schedule bar
      this.$refs.orderPopover.displayFromElement(this.$refs.scheduleLeftBar, evt.order)
    }
  }
}
</script>
